import { useState } from "react";
import {
  ArrowUpRight,
  Code2,
  Smartphone,
  Compass,
  BrainCircuit,
  Workflow,
  Wrench,
  Layers3,
} from "lucide-react";
import { Reveal, ScrollHeading, ScrollDepth, Eyebrow } from "./ui";
import { EnterpriseConcept, MobileConcept } from "./service-concepts";
const services = [
  {
    title: "Software Consulting",
    description:
      "Clarify requirements, review an existing system, or plan a new product. We help you choose an approach, technology stack, and delivery plan that fit your goals.",
    icon: Compass,
    kind: "consulting",
    category: "Consulting",
  },
  {
    title: "Web Applications",
    description:
      "Web applications, customer portals, and dashboards built around your users and business processes. We turn requirements into useful, accessible software.",
    icon: Code2,
    kind: "web",
    category: "Development",
  },
  {
    title: "Custom Software Platforms",
    description:
      "Internal tools, workflow automation, and business platforms tailored to your operations, with room to grow as requirements change.",
    icon: Layers3,
    kind: "enterprise",
    category: "Development",
  },
  {
    title: "Mobile Applications",
    description:
      "Mobile apps for customer-facing services or internal teams, with features and integrations shaped around how people use them.",
    icon: Smartphone,
    kind: "mobile",
    category: "Development",
  },
  {
    title: "AI Integration & Automation",
    description:
      "Add AI to the tools your business already uses: internal assistants, document workflows, smarter search, and task automation. We help select, integrate, and test solutions around your business needs.",
    icon: BrainCircuit,
    kind: "ai",
    category: "Development",
  },
  {
    title: "Integrations & Modernization",
    description:
      "Connect your applications and data, improve existing code, or modernize older systems. We help you plan and deliver changes in manageable steps.",
    icon: Workflow,
    kind: "modernization",
    category: "Improvement & support",
  },
  {
    title: "Maintenance & Support",
    description:
      "Bug fixes, performance improvements, and new features for the software your business relies on. Support scope and availability are agreed for each engagement.",
    icon: Wrench,
    kind: "support",
    category: "Improvement & support",
  },
];
export function Services() {
  const [filter, setFilter] = useState("All services");
  const filters = [
    "All services",
    "Consulting",
    "Development",
    "Improvement & support",
  ];
  const visible = services.filter(
    (service) => filter === "All services" || service.category === filter,
  );

  return (
    <section id="services" className="services section-pad">
      <div className="wrap">
        <Reveal className="services-heading">
          <div>
            <Eyebrow number="02">How we can help</Eyebrow>
            <ScrollHeading
              lines={[
                { text: "From ideas", muted: false },
                { text: "to software.", muted: true },
              ]}
            />
          </div>
          <p>
            Bring us in for early advice, a complete build, or improvements to
            existing software. We shape the work around your priorities and the
            support your team needs.
          </p>
        </Reveal>
        <div
          className="service-filters"
          role="group"
          aria-label="Filter services"
        >
          {filters.map((f) => (
            <button
              key={f}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
              className={filter === f ? "selected" : ""}
            >
              {f}
            </button>
          ))}
        </div>
        <div
          className="service-showcase"
          hidden={filter !== "All services" && filter !== "Development"}
        >
          <ScrollDepth distance={20}>
            <Reveal className="showcase-card">
              <EnterpriseConcept />
              <div className="showcase-caption">
                <span>Custom software platforms</span>
                <Layers3 size={19} strokeWidth={1.4} />
              </div>
            </Reveal>
          </ScrollDepth>
          <ScrollDepth distance={-20}>
            <Reveal className="showcase-card" delay={0.1}>
              <MobileConcept />
              <div className="showcase-caption">
                <span>Mobile applications</span>
                <Smartphone size={19} strokeWidth={1.4} />
              </div>
            </Reveal>
          </ScrollDepth>
        </div>
        <div className="services-grid" aria-live="polite">
          {visible.map(({ title, description, icon: Icon, kind }, index) => (
            <Reveal key={kind} className="service-card" delay={index * 0.045}>
              <a
                href={`mailto:quantumcodecorp@gmail.com?subject=${encodeURIComponent(title + " enquiry")}`}
                className="service-card-link"
                aria-label={`Discuss ${title}`}
              >
                <span className="service-index">
                  0{services.findIndex((s) => s.kind === kind) + 1}
                </span>
                <div className="service-title">
                  <Icon size={24} strokeWidth={1.4} />
                  <h3>{title}</h3>
                </div>
                <p>{description}</p>
                <ArrowUpRight className="service-arrow" size={23} />
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
